<script lang="ts" setup>
// ================================
// IMPORTS
// ================================

// Vue
import { computed, ref, useId, inject } from "vue"
import { useI18n } from "vue-i18n"

// Composables
import { useFetchWrapper, useRedirect } from "@core/composables"
import { useEventBus } from "@vueuse/core"

// Services
import type { Services } from "@services"

// Stores
import { useAuthStore } from "@core/stores/auth.store"

// Constants
import {
    CONSENT_REQUEST_STATUS,
    CONSENT_REQUEST_TYPE,
    SOURCE_TYPE_CATEGORIES,
} from "@core/constants"

// Helpers
import { formatIdSource, formatDate } from "@core/helpers"

// PrimeVue
import { ToastMessageOptions, useConfirm, useToast } from "primevue"

// Types
import type { NotificationEventTask, ConsentRequest } from "@types"
import { ColumnConfig } from "@types"

/** Entrée d’email dans le tracking (même forme que les mails racine éventuels). */
type ConsentRequestMail = ConsentRequest["tracking"]["mails"][number]

/** Demande API avec champs optionnels non modélisés partout sur le type strict. */
type ConsentRequestListItem = ConsentRequest & {
    mails?: ConsentRequestMail[]
}

/** Ligne affichée dans le tableau (demande + champs dérivés). */
type ConsentRequestTableRow = ConsentRequestListItem & {
    sourceIdsource: string
    sourceIdtype: string
    buildingName: string
    sourceName: string
    titulaire: string
    titulaireForFilter: string
    status: string
    statusForFilter: string
    sourceTypeForFilter: string
    _id_source: string | undefined
    _id_building: string | null | undefined
    canClickSource: boolean
    searchIdsource: string
    searchType: string
    searchSourceName: string
    searchBuildingName: string
    searchTitulaire: string
}

// ================================
// INTERNATIONALIZATION
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            delete: {
                button: "Supprimer",
                confirm: "Êtes-vous sûr de vouloir supprimer la demande ?",
                info: "Cette action est irréversible.",
                success: "La demande a bien été supprimée.",
                error: "Une erreur est survenue lors de la suppression.",
                cancel: "Annuler",
                accept: "Supprimer",
            },
            compteur: "Compteur",
            type: "Type",
            titulaire: "Titulaire",
            batiment: "Bâtiment associé",
            /** Libellé quand le bâtiment existe mais n’a pas de nom (colonne lien + menu clic droit dans le tableau) */
            batimentSansNom: "Sans nom — clic droit pour le menu",
            status: "Avancement",
            status_consent: ({ named }: { named: (key: string) => string }) => {
                const status = {
                    [CONSENT_REQUEST_STATUS.CREATED]: "Demande formulée",
                    [CONSENT_REQUEST_STATUS.ACCEPTED]: "Demande acceptée",
                    [CONSENT_REQUEST_STATUS.WAITING_GRDF]: "Validation GRDF",
                    [CONSENT_REQUEST_STATUS.REFUSED]: "Demande refusée",
                    [CONSENT_REQUEST_STATUS.REMOVED]: "Demande supprimée",
                    [CONSENT_REQUEST_STATUS.EXPIRED]: "Demande expirée",
                    [CONSENT_REQUEST_STATUS.TERMINATED]: "Demande terminée",
                }
                const state = status[named("status")] ?? "Statut inconnu"
                if (
                    state === "Demande formulée" &&
                    named("send") &&
                    !named("relance")
                )
                    return `Envoyée le ${named("date")}`
                else if (state === "Demande formulée" && named("relance"))
                    return `Relancée le ${named("date")}`
                return state
            },
            menu: {
                delete: "Supprimer la demande",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================

// ================================
// PROPS & EMITS
// ================================

const props = defineProps<{
    consentRequests: ConsentRequestListItem[]
    /** Si true, affiche la colonne « Bâtiment associé » (ex. onglet signature SIGN/SHARE). False pour EXTEND / CHANGE_OWNER. */
    isConsentRequestSign: boolean
    rowsPerPage: number
}>()

const emit = defineEmits<{
    "reload:consent": []
    "delete:consent": [consentRequestId: string]
    "update:consent-request": [consentRequest: ConsentRequest]
}>()

// ================================
// SERVICES & STORES
// ================================

/**
 * Services injectés
 */
const { $deleteConsentRequest } = inject<Services>("services")!

/**
 * Store d'authentification pour vérifier les permissions utilisateur
 */
const authStore = useAuthStore()
const { redirectWrapper } = useRedirect()

/**
 * Service de confirmation pour les actions destructives
 */
const confirm = useConfirm()

/**
 * Bus d'événements pour les notifications de tâches
 */
const busTask = useEventBus<NotificationEventTask>("task")

/**
 * Service de notifications toast
 */
const toast = useToast()

// ================================
// ÉTAT DU TABLEAU
// ================================

/**
 * Lignes du tableau actuellement étendues
 */
const expandedRows = defineModel<Record<string, boolean>>("expandedRows", {
    required: true,
})

/**
 * Identifiant unique du composant pour les dialogs de confirmation
 */
const componentId = useId()

// ================================
// ÉTAT DU MENU CONTEXTUEL
// ================================

/**
 * Référence au composant menu contextuel
 */
const menu = ref()

/**
 * Demande de consentement sélectionnée pour le menu contextuel
 */
const selectedConsentRequest = ref<ConsentRequest | null>(null)

// ================================
// DONNÉES CALCULÉES
// ================================

/**
 * Prépare les données pour le tableau en aplatissant les champs imbriqués
 * @returns Données formatées pour le tableau
 */
const getTableData = computed((): ConsentRequestTableRow[] => {
    return props.consentRequests.map((request: ConsentRequestListItem) => {
        const sourceIdsource = formatIdSource(
            request?.source?.idsource,
            request?.source?.idtype
        )
        const sourceIdtype = request?.source?.idtype?.toUpperCase() ?? ""
        const buildingNameRaw = (request?.building?.name ?? "").trim()
        const hasBuildingLink = Boolean(request?.building?._id)
        const buildingName =
            buildingNameRaw || (hasBuildingLink ? t("batimentSansNom") : "-")
        const sourceName = request?.source?.name ?? ""

        const userData = request.user_to
        const titulaire =
            userData?.company?.name ??
            (`${userData?.firstname || ""} ${userData?.lastname || ""}`.trim() ||
                "-")

        const status = t("status_consent", {
            status: request?.state,
            send: hasSentInvitation(request),
            relance: hasRelanceMails(request),
            date: getLastMailDate(request),
        })

        // Détermine le type de compteur pour le filtre (catégorie)
        const sourceTypeForFilter = getSourceTypeCategory(
            request?.source?.idtype
        )

        // Détermine le titulaire pour le filtre
        const titulaireForFilter = getTitulaireCategory(request)

        // Détermine le statut pour le filtre
        const statusForFilter = getStatusCategory(request)

        // Champs de recherche (idsource sans espaces pour la recherche)
        const searchIdsource = (request?.source?.idsource || "").replace(
            /\s+/g,
            ""
        )

        return {
            ...request,
            sourceIdsource,
            sourceIdtype,
            buildingName,
            sourceName,
            titulaire,
            titulaireForFilter, // Pour le filtre
            status,
            statusForFilter, // Pour le filtre
            sourceTypeForFilter, // Pour le filtre
            _id_source: request?.source?._id,
            _id_building: request?.building?._id,
            canClickSource: request.type != CONSENT_REQUEST_TYPE.SIGN,
            // Champs de recherche pour la recherche globale
            searchIdsource, // idsource sans espaces
            searchType: sourceIdtype, // Type de source
            searchSourceName: sourceName, // Nom de la source
            searchBuildingName: buildingName, // Nom du bâtiment
            searchTitulaire: titulaire, // Nom du titulaire
        }
    })
})

/**
 * Options de filtre pour le type de compteur (catégories)
 * @returns Liste des options de filtre pour le type
 */
const getTypeFilterOptions = computed(() => {
    return Object.values(SOURCE_TYPE_CATEGORIES).map((category) => ({
        label: category.label,
        value: category.label,
    }))
})

/**
 * Options de filtre pour le titulaire
 * @returns Liste des options de filtre pour le titulaire
 */
const getTitulaireFilterOptions = computed(() => {
    return [
        { label: "Vous", value: "vous" },
        { label: "Personne physique", value: "personne_physique" },
        { label: "Personne morale", value: "personne_morale" },
    ]
})

/**
 * Options de filtre pour le statut
 * @returns Liste des options de filtre pour le statut
 */
const getStatusFilterOptions = computed(() => {
    return [
        { label: "Formulaire", value: "formulaire" },
        { label: "Signature", value: "signature" },
        { label: "Validation GRDF", value: "validation_grdf" },
    ]
})

/**
 * Configuration des colonnes du tableau de demandes de consentement
 * @returns Configuration des colonnes avec leurs transformations et actions
 */
const getColumns = computed<ColumnConfig[]>(() => {
    const baseColumns: ColumnConfig[] = [
        {
            field: "sourceIdsource",
            header: `${t("compteur")} (${props.consentRequests?.length})`,
            style: { width: "12rem" },
        },
        {
            field: "sourceTypeForFilter",
            header: t("type"),
            style: { width: "4rem" },
            filterable: true,
            filterOptions: getTypeFilterOptions.value,
        },
        {
            field: "titulaireForFilter",
            header: t("titulaire"),
            style: { width: "10rem" },
            filterable: true,
            filterOptions: getTitulaireFilterOptions.value,
        },
    ]

    // Colonne bâtiment : uniquement quand le parent affiche l’onglet signature (SIGN/SHARE)
    if (props.isConsentRequestSign) {
        baseColumns.push({
            field: "buildingName",
            header: t("batiment"),
            link: true,
            style: { width: "11rem" },
        })
    }

    // Add status column
    baseColumns.push({
        field: "statusForFilter",
        header: t("status"),
        style: { width: "12rem" },
        filterable: true,
        filterOptions: getStatusFilterOptions.value,
    })

    // Add actions column
    baseColumns.push({
        field: "actions",
        header: "",
        style: { width: "5rem" },
    })

    // Colonnes cachées pour la recherche globale
    baseColumns.push(
        {
            field: "searchIdsource",
            header: "",
            style: {
                width: "0",
                padding: "0",
                visibility: "hidden",
            } as Record<string, string>,
        },
        {
            field: "searchType",
            header: "",
            style: {
                width: "0",
                padding: "0",
                visibility: "hidden",
            } as Record<string, string>,
        },
        {
            field: "searchSourceName",
            header: "",
            style: {
                width: "0",
                padding: "0",
                visibility: "hidden",
            } as Record<string, string>,
        },
        {
            field: "searchBuildingName",
            header: "",
            style: {
                width: "0",
                padding: "0",
                visibility: "hidden",
            } as Record<string, string>,
        },
        {
            field: "searchTitulaire",
            header: "",
            style: {
                width: "0",
                padding: "0",
                visibility: "hidden",
            } as Record<string, string>,
        }
    )

    return baseColumns
})

/**
 * Éléments du menu contextuel pour les demandes de consentement
 * @returns Liste des actions disponibles dans le menu contextuel
 */
const getMenuItems = computed(() => [
    {
        label: t("menu.delete"),
        command: onDeleteRequest,
    },
])

// ================================
// LOGIQUE MÉTIER
// ================================

/**
 * Détermine la catégorie du type de source pour le filtre
 * @param idtype - Type de source (pdl, pce, etc.)
 * @returns Label de la catégorie (Électricité, Gaz, RCU)
 */
function getSourceTypeCategory(idtype: string): string {
    if (!idtype) return ""
    const category = Object.values(SOURCE_TYPE_CATEGORIES).find((cat) =>
        cat.types.includes(idtype)
    )
    return category?.label || ""
}

/**
 * Détermine la catégorie du titulaire pour le filtre
 * @param request - Demande de consentement à analyser
 * @returns Catégorie du titulaire (vous, personne_physique, personne_morale)
 */
function getTitulaireCategory(request: ConsentRequestListItem): string {
    const isCurrentUser =
        request.user_to?._id === authStore.userId && !request?.user_to?.company
    const isCurrentCompany =
        request?.user_to?.company?._id === authStore.companyId &&
        request?.user_to?.company

    if (isCurrentUser || isCurrentCompany) {
        return "vous"
    }

    if (!request?.user_to?.company) {
        return "personne_physique"
    }

    if (request?.user_to?.company) {
        return "personne_morale"
    }

    return ""
}

/**
 * Détermine la catégorie du statut pour le filtre
 * @param request - Demande de consentement à analyser
 * @returns Catégorie du statut (formulaire, signature, validation_grdf)
 */
function getStatusCategory(request: ConsentRequestListItem): string {
    // Pour les demandes multiples, le tracking se trouve dans _id_consentrequestmulti
    const isMultiRequest = !!request?._id_consentrequestmulti
    const mails = isMultiRequest
        ? request._id_consentrequestmulti?.tracking?.mails || []
        : request?.tracking?.mails || []

    const isFormulaire =
        request.state === CONSENT_REQUEST_STATUS.CREATED && mails.length === 0

    const isSignature =
        (request.state === CONSENT_REQUEST_STATUS.CREATED &&
            mails.length > 0) ||
        request.state === CONSENT_REQUEST_STATUS.ACCEPTED

    const isValidationGrdf =
        request.state === CONSENT_REQUEST_STATUS.WAITING_GRDF

    if (isFormulaire) return "formulaire"
    if (isSignature) return "signature"
    if (isValidationGrdf) return "validation_grdf"

    return ""
}

/**
 * Vérifie si la demande contient des emails de relance
 * @param request - Demande de consentement à vérifier
 * @returns True si la demande contient des emails de relance
 */
function hasRelanceMails(request: ConsentRequestListItem): boolean {
    // Pour les demandes multiples, le tracking se trouve dans _id_consentrequestmulti
    const isMultiRequest = !!request?._id_consentrequestmulti
    const mails = isMultiRequest
        ? request._id_consentrequestmulti?.tracking?.mails || []
        : request?.tracking?.mails || []

    return mails.some((m) => m.type === "relance") ?? false
}

/**
 * Récupère la date du dernier email envoyé
 * @param request - Demande de consentement
 * @returns La date formatée du dernier email ou null
 */
function getLastMailDate(request: ConsentRequestListItem): string | null {
    // Pour les demandes multiples, le tracking se trouve dans _id_consentrequestmulti
    const isMultiRequest = !!request?._id_consentrequestmulti
    const mails = isMultiRequest
        ? request._id_consentrequestmulti?.tracking?.mails || []
        : request?.tracking?.mails || []

    if (mails.length === 0) return null

    const sortedMails = [...mails].sort(
        (a: ConsentRequestMail, b: ConsentRequestMail) =>
            new Date(b.date).getTime() - new Date(a.date).getTime()
    )

    return formatDate(sortedMails[0]?.date) ?? null
}

/**
 * Vérifie si une invitation a été envoyée pour la demande
 * @param request - Demande de consentement à vérifier
 * @returns True si une invitation a été envoyée
 */
function hasSentInvitation(request: ConsentRequestListItem): boolean {
    const hasInvitationMail =
        request.mails?.some((m) => m.type === "invitation") ?? false
    const hasMultiTracking =
        (request._id_consentrequestmulti?.tracking?.mails?.length ?? 0) > 0
    return hasInvitationMail || hasMultiTracking
}

// ================================
// INTERACTIONS UTILISATEUR
// ================================

/**
 * Gère le clic sur une cellule du tableau
 * @param payload - Données du clic (data, field, rowIndex, _blank)
 */
function onClickCell(payload: {
    data: ConsentRequestTableRow
    field: string
    rowIndex: number
    _blank?: boolean
}): void {
    const { data, field } = payload
    if (
        field === "sourceIdsource" &&
        data?.canClickSource &&
        data?._id_source
    ) {
        redirectWrapper("/source/:_id_source/data", {
            _id_source: data._id_source,
        })
    } else if (field === "buildingName" && data?._id_building) {
        redirectWrapper("/building/:_id_building/compteur", {
            _id_building: data._id_building,
        })
    }
}

/**
 * Bascule l'état d'expansion d'une ligne
 * @param data - Données de la ligne à basculer
 */
function onToggleRow(data: ConsentRequestTableRow): void {
    const id = data._id
    if (expandedRows.value[id]) {
        delete expandedRows.value[id]
    } else {
        expandedRows.value[id] = true
    }
}

/**
 * Ouvre le menu contextuel pour une demande
 * @param event - Événement de clic
 * @param consent - Demande de consentement sélectionnée
 */
function onToggleMenu(event: Event, consent: ConsentRequestListItem): void {
    selectedConsentRequest.value = consent
    menu.value.toggle(event)
}

/**
 * Lance la suppression de la demande sélectionnée
 */
function onDeleteRequest(): void {
    onConfirmDelete()
}

/**
 * Affiche la confirmation de suppression et gère la suppression de la demande
 */
function onConfirmDelete(): void {
    confirm.require({
        group: componentId,
        message: t("delete.info"),
        header: t("delete.confirm"),
        icon: "pi pi-info-circle",
        rejectLabel: t("delete.cancel"),
        rejectProps: {
            label: t("delete.reject"),
            outlined: true,
            severity: "secondary",
        },
        acceptProps: {
            label: t("delete.accept"),
            icon: "pi pi-trash",
            severity: "danger",
        },
        accept: async () => {
            const _id_consentrequest = selectedConsentRequest?.value?._id
            if (!_id_consentrequest) return

            const {
                fetch: fetchDeleteConsentRequest,
                isFetching: isFetchingDeleteConsentRequest,
            } = useFetchWrapper(async () => {
                if (!$deleteConsentRequest) {
                    throw new Error(
                        "Service $deleteConsentRequest non disponible"
                    )
                }

                try {
                    const typeMapping: Record<
                        string,
                        "sign" | "share" | "extend" | "change-owner"
                    > = {
                        SIGN: "sign",
                        SHARE: "share",
                        EXTEND: "extend",
                        CHANGE_OWNER: "change-owner",
                        // valeur par défaut : "sign"
                    }
                    const originalType = selectedConsentRequest?.value?.type
                    const serviceType =
                        typeMapping[originalType?.toUpperCase?.() ?? ""] ??
                        "sign"
                    await $deleteConsentRequest(_id_consentrequest, serviceType)
                } catch (error) {
                    console.error(
                        "Erreur lors de la suppression de la demande de consentement:",
                        error
                    )
                    throw error
                }
                // Émet l'événement avec l'ID de la demande supprimée pour suppression locale
                emit("delete:consent", _id_consentrequest)
            })

            const promise = fetchDeleteConsentRequest()
            let notification: ToastMessageOptions | null = null

            function callback(_notification: ToastMessageOptions) {
                notification = _notification
            }
            // Affiche la notification de tâche en chargement
            if (isFetchingDeleteConsentRequest.value) {
                busTask.emit({
                    severity: "info",
                    title: "Suppression en cours",
                    message:
                        "La demande est en cours de suppression, veuillez patienter...",
                    callback,
                })
            }
            if (promise) {
                await promise
                if (notification) toast.remove(notification)
            }
        },
    })
}

/**
 * Quand la demande de consentement est supprimée, on émet l'événement pour supprimer la demande de consentement locale
 * @param _id_consentrequest - ID de la demande de consentement à supprimer
 */
function onDeleteConsent(_id_consentrequest: string | undefined): void {
    if (!_id_consentrequest) return
    emit("delete:consent", _id_consentrequest)
}
</script>

<template>
    <BaseTable
        :value="getTableData"
        :columns="getColumns"
        data-key="_id"
        paginator
        :rows="rowsPerPage"
        :rows-per-page-options="[10, 20, 50]"
        v-model:expanded-rows="expandedRows"
        @click:cell="onClickCell"
        search
    >
        <!-- ================================ -->
        <!-- COLONNE TYPE -->
        <!-- ================================ -->
        <template #cell_sourceTypeForFilter="{ data }">
            <p
                class="pr-4 base-table-link-label whitespace-nowrap overflow-hidden text-ellipsis"
            >
                {{ data.sourceIdtype }}
            </p>
        </template>

        <!-- ================================ -->
        <!-- COLONNE TITULAIRE -->
        <!-- ================================ -->
        <template #cell_titulaireForFilter="{ data }">
            <p
                class="pr-4 base-table-link-label whitespace-nowrap overflow-hidden text-ellipsis"
            >
                {{ data.titulaire }}
            </p>
        </template>

        <!-- ================================ -->
        <!-- COLONNE STATUT -->
        <!-- ================================ -->
        <template #cell_statusForFilter="{ data }">
            <p
                class="pr-4 base-table-link-label whitespace-nowrap overflow-hidden text-ellipsis"
            >
                {{ data.status }}
            </p>
        </template>
        <!-- ================================ -->
        <!-- COLONNE ACTIONS -->
        <!-- ================================ -->
        <template #cell_actions="{ data }">
            <div class="flex items-center justify-end">
                <BaseButton
                    :icon="
                        expandedRows[data._id]
                            ? 'pi pi-chevron-up'
                            : 'pi pi-chevron-down'
                    "
                    text
                    rounded
                    @click="onToggleRow(data)"
                />
                <BaseButton
                    variant="link"
                    size="small"
                    @click="onToggleMenu($event, data)"
                    icon="pi pi-ellipsis-h"
                />
            </div>
        </template>

        <!-- ================================ -->
        <!-- CONTENU D'EXPANSION -->
        <!-- ================================ -->
        <template #expansion="{ data }">
            <div class="p-8">
                <ConsentRequestStatus
                    :consent-request="data"
                    @delete:consent="onDeleteConsent"
                    @update:consent-request="
                        (cr) => emit('update:consent-request', cr)
                    "
                />
            </div>
        </template>
    </BaseTable>

    <!-- ================================ -->
    <!-- MENU CONTEXTUEL -->
    <!-- ================================ -->
    <BaseMenu ref="menu" :model="getMenuItems" :popup="true" />

    <!-- ================================ -->
    <!-- DIALOG DE CONFIRMATION -->
    <!-- ================================ -->
    <BaseConfirmDialog :group="componentId"></BaseConfirmDialog>
</template>

<style scoped>
/* ================================ */
/* GESTION DU TEXTE ELLIPSIS */
/* ================================ */
:deep(.text-ellipsis) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
}

:deep(.p-datatable-tbody > tr > td.text-ellipsis) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* ================================ */
/* ESPACEMENT DES CELLULES */
/* ================================ */
:deep(.p-datatable-tbody > tr > td) {
    padding: 0.3rem 0.75rem;
}

:deep(.p-datatable-thead > tr > th) {
    padding: 0.3rem 0.75rem;
}
</style>
